import { DateRange } from "@/dateRanges/dateRanges";
import { WindowSize } from "@/dateRanges/windowSizes";
import { Metric } from "@/metrics/installationMetrics";
import { OpportunityCategory } from "@/opportunity/opportunity";
import { TSMetric } from "@/query/query";
import { User } from "@/user/user";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { apiClient } from "./client";
import { AchievementCategory, User as MarioUser } from "@/views/mario/marioTypes";
import { createQueryKeys } from "@lukemorales/query-key-factory";

interface ResetCredentials {
  token: string;
  password: string;
}

interface Email {
  email: string;
}

interface ApiResponse extends Record<string, any> {
  error?: any;
}

export interface PaginatedResponse<ItemType = any> {
  count: number
  results: ItemType[]
  page_size: number
  next: string
  previous: string
}

export type QueryProps = {
  dateRange: DateRange;
  windowSize: WindowSize;
  installation_id?: any;
  location_id?: any;
  organization_id?: any;
  equipment_id?: number;
  /** Legacy metric from thalo_data_metric_calculation */
  metric?: Metric["name"];
  /** New: Array of TSMetric names for multi-metric queries (e.g., ["CO2_INPUT", "CO2_OUTPUT"]) */
  metrics?: TSMetric[];
  target_unit?: string;  // todo: refine
  normalize?: any;
};

export const sendResetCredentialsRequest = async (
  email: Email
): Promise<ApiResponse | undefined> => {
  try {
    const res = await apiClient.post("/auth/passwordreset/", email);
    return res?.data?.status;
  } catch (err: any) {
    return { error: err?.response?.data?.email };
  }
};

export const confirmResetCredentials = async (
  credentials: ResetCredentials
): Promise<ApiResponse | undefined> => {
  try {
    const res = await apiClient.post("/password_reset/confirm/", credentials);
    // Backend now returns tokens along with status for auto-login
    return res?.data;
  } catch (err: any) {
    return { error: err?.response?.data?.password };
  }
};

export const post = async (
  path: string,
  body: Record<string, any>
): Promise<ApiResponse | null> => {
  const res = await apiClient.post(path, body);
  if (res.data.length === 0) return null;
  return await res.data;
};

export const get = async <T extends Record<string, any>>(path: string): Promise<T> => {
  const res = await apiClient.get<T>(path);
  // if (res.data.length === 0) return null;
  return res.data;
};

declare global {
  interface Window {
    Appcues?: any;
  }
}

export const users = createQueryKeys("users", {
  me: () => ({
    queryKey: ["me"],
    queryFn: async () => {
      const res = await get<User>("users/me/")
      window.Appcues?.identify(
        res.username,
        {
          email: res.email,
        },
        {
          name: res.first_name,
          last_name: res.last_name,
        }
      );
      return res
    }
  }
)})

export function useUser() {
  return useQuery(users.me());
}

export const useOpportunityCategories = ({
  page = 1,
  pageSize = 100,
}: {
  page?: number;
  pageSize?: number;
}) => {
  const url = `opportunity-categories/?page=${page}&page_size=${pageSize}`;

  return useSuspenseQuery({
    queryKey: ["opportunity-categories", page, pageSize],
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      return await get<PaginatedResponse<OpportunityCategory>>(url);
    },
  });
};

export const useRoiCalculation = (params: {
  squareFootage: number;
  annualEnergyConsumption: number;
  climateZone: string;
  climateRegion: string;
  numberOfCoolingUnits: number;
  numberOfHeatingUnits: number;
  primaryHeatingMethod: string;
  years: number;
  perUnitRefrigerantInstallationCost: number;
  baseAnnualSubscriptionCost: number;
  annualOnSiteRepairCostPerUnit: number;
  buildingAddress: string;
  name?: string;
  role?: string;
  company?: string;
  email?: string;
  phoneNumber?: string;
}) => {
  return useQuery({
    queryKey: ['roi-calculation', params],
    queryFn: async () => {
      const res = await apiClient.post("/roi/calculate/", params);
      return res.data;
    },
    enabled: false,
  });
};

export const fetchClimateOptions = async () => {
  const res = await apiClient.get("/roi/calculate/");
  return res.data;
};

// Fetch the leaderboard data
export function useLeaderboardData({
  page = 1,
  pageSize = 10,
  sorting = [],
  filters = {},
}: {
  page?: number;
  pageSize?: number;
  sorting?: SortingState;
  filters?: {
    achievement_category?: string;
  };
}) {
  let url = `/mario/leaderboard/?page=${page}&page_size=${pageSize}`;

  if (sorting.length) {
    const order = sorting[0]?.desc ? `-${sorting[0]?.id}` : sorting[0]?.id;
    url += `&ordering=${order}`;
  }

  if (filters.achievement_category) {
    url += `&achievements__category__name__icontains=${filters.achievement_category}`;
  }

  return useQuery({
    queryKey: ["leaderboard", page, pageSize, sorting, filters],
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await apiClient.get<PaginatedResponse<User>>(url);
      return res.data;
    },
  });
}

// Fetch user details
export function useUserDetails(userId: number | string) {
  return useQuery({
    queryKey: ["user-details", userId],
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!userId,
    queryFn: async () => {
      return await get<MarioUser>(`/mario/users/${userId}/`);
    },
  });
}

// Fetch achievement categories (if needed)
export function useAchievementCategories({
  page = 1,
  pageSize = 100,
}: {
  page?: number;
  pageSize?: number;
}) {
  const url = `/mario/achievement-categories/?page=${page}&page_size=${pageSize}`;

  return useQuery({
    queryKey: ["achievement-categories", page, pageSize],
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      return await get<PaginatedResponse<AchievementCategory>>(url);
    },
  });
}

export async function getTrendRateOfChange({
  installation_id,
  start_date,
  end_date,
  window_hours = "24",
  rate_of_change_denominator = "day",
}: {
  installation_id: string;
  start_date: string; // YYYY-MM-DD
  end_date: string;   // YYYY-MM-DD
  window_hours?: string | number;
  rate_of_change_denominator?: string;
}) {

  const params = new URLSearchParams({
    installation_id,
    start_date,
    end_date,
    window_hours: window_hours.toString(),
    rate_of_change_denominator,
  });
  
  // Make direct API call to get raw response with Content-Type header
  const response = await apiClient.get(`trend/rate-of-change/?${params.toString()}`, {});
  return response.data;
}